import type { ComponentType, ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TablePagination } from '@/components/TablePagination';
import { TableEmptyRow } from '@/components/TableEmptyRow';
import { cn } from '@/lib/utils';

export interface DataColumn<T> {
  id: string;
  /** Text, or a header control such as a ColumnFilter. */
  header: ReactNode;
  className?: string;
  render: (item: T) => ReactNode;
}

interface DataTableProps<T> {
  items: T[];
  columns: DataColumn<T>[];
  getKey: (item: T) => string;
  /** Extra classes for a row, e.g. greyed-out closed loans. */
  rowClassName?: (item: T) => string | undefined;
  emptyIcon: ComponentType<{ className?: string }>;
  emptyTitle: string;
  /** Word after the count in the pager, e.g. "loans". */
  itemLabel: string;
  currentPage: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  /** Sort arrows on the first column (ID), the only sortable column. null = API order. */
  firstColumnSort?: { direction: SortDirection | null; onToggle: () => void };
}

export type SortDirection = 'asc' | 'desc';

/** Clicking a sort header cycles: API order → ascending → descending → API order. */
export const nextSortDirection = (current: SortDirection | null): SortDirection | null =>
  current === null ? 'asc' : current === 'asc' ? 'desc' : null;

/** Flat page table (Loans, Balances) with a sticky header and the shared pager. */
export function DataTable<T>({
  items,
  columns,
  getKey,
  rowClassName,
  emptyIcon: EmptyIcon,
  emptyTitle,
  itemLabel,
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  firstColumnSort,
}: DataTableProps<T>) {
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-sm">
      <div className="min-h-0 flex-1 overflow-auto scrollbar-hide group-data-[table-locked=true]/page:overflow-y-hidden">
        <Table containerClassName="overflow-visible">
          <TableHeader className="sticky top-0 z-10 bg-gray-50/95 backdrop-blur">
            <TableRow className="border-gray-200 hover:bg-transparent">
              {columns.map((column, index) => (
                <TableHead key={column.id} className={cn('h-11 px-4 text-xs font-semibold text-gray-800', column.className)}>
                  {index === 0 && firstColumnSort ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-ml-2 h-8 gap-1 px-2 text-xs font-semibold tracking-wide text-gray-800 hover:bg-gray-100"
                      onClick={firstColumnSort.onToggle}
                    >
                      {column.header}
                      {firstColumnSort.direction === 'asc' ? (
                        <ArrowUp className="h-3.5 w-3.5 text-[#4369a0]" />
                      ) : firstColumnSort.direction === 'desc' ? (
                        <ArrowDown className="h-3.5 w-3.5 text-[#4369a0]" />
                      ) : (
                        <ArrowUpDown className="h-3.5 w-3.5 text-gray-400" />
                      )}
                    </Button>
                  ) : (
                    column.header
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && <TableEmptyRow columnCount={columns.length} icon={EmptyIcon} title={emptyTitle} />}
            {items.map((item) => (
              <TableRow key={getKey(item)} className={cn('border-gray-100 text-[13px]', rowClassName?.(item))}>
                {columns.map((column) => (
                  <TableCell key={column.id} className={cn('px-4 py-3', column.className)}>
                    {column.render(item)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        itemLabel={itemLabel}
      />
    </div>
  );
}
